import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { useLoaderData } from 'react-router';

import { canonicalTag } from '@jane/bloom/resources/meta/canonicalTag';
import { description } from '@jane/bloom/resources/meta/description';
import { getRootLoaderData } from '@jane/bloom/resources/meta/getRootLoaderData';
import { parentMeta } from '@jane/bloom/resources/meta/parentMeta';
import { title } from '@jane/bloom/resources/meta/title';
import { getEmailSession } from '@jane/bloom/sessions/email.server';
import type { RouteHandle } from '@jane/bloom/types/schemas/bloom/routeHandle';
import { ensureBloomMenu } from '@jane/bloom/utils/ensureBloomMenu.server';

import { SignIn } from './components/signIn';

export const handle: RouteHandle = {
  hydrate: true,
  scripts: () => [
    /** @see https://developers.google.com/identity/gsi/web/guides/client-library */
    {
      async: true,
      defer: true,
      src: 'https://accounts.google.com/gsi/client',
    },
  ],
};

export const meta: MetaFunction<typeof loader> = (args) => {
  const {
    menuUrl,
    metaProps: {
      fulfillmentMode,
      pinnedStoreCity,
      pinnedStoreName,
      pinnedStoreState,
      reservationMode,
    },
  } = getRootLoaderData(args);

  return [
    title(
      `Let's check if you have an account`,
      `${pinnedStoreName} Dispensary ${pinnedStoreCity}, ${pinnedStoreState}`
    ),
    description(
      `Visit ${pinnedStoreName} dispensary in ${pinnedStoreCity}, ${pinnedStoreState} and order ${fulfillmentMode} cannabis online products for ${reservationMode}. Browse our online dispensary menu for flower, edibles, vape and more.`
    ),
    canonicalTag(args.location, menuUrl),
    ...parentMeta(args),
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const { cookie_prefix, google_sso_client_id: googleSsoClientId } =
    await ensureBloomMenu(request);
  const { getSession } = getEmailSession(cookie_prefix);
  const emailSession = await getSession(request.headers.get('Cookie'));
  const userEmail = emailSession.get('email')?.email;

  return {
    googleSsoClientId,
    userEmail,
  };
}

export default function LoginRoute() {
  const { googleSsoClientId, userEmail } = useLoaderData<typeof loader>();

  return <SignIn googleSsoClientId={googleSsoClientId} userEmail={userEmail} />;
}
